import { fold } from 'fp-ts/lib/Either';
import JSZip from 'jszip';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import { configStepAdapter } from '../../adapters/ConfigStepAdapter';
import { ParameterToParameterWithTitleAdapter } from '../../adapters/ParameterToParameterWithTitleAdapter';
import { FullPrescription, LensPackage } from '../../meta/IBrain';
import { Parameter } from '../../models/Parameter';
import { Step } from '../../models/Step';
import { getCheapPackageFromList } from './prices';

export function hideStepSkeleton() {
  const stepSkeleton = document.querySelector('.tw-step-skeleton');
  if (stepSkeleton) {
    stepSkeleton.style.display = 'none';
  }
}

export function showStepSkeleton() {
  const stepSkeleton = document.querySelector('.tw-step-skeleton');
  if (stepSkeleton) {
    stepSkeleton.style.display = 'flex';
  }
}

const isFrameOnlySelected = (configPackages, configData) => {
  let configFrameOnly = getFrameOnlyKey(configPackages, configData);
  let typeSelection = window.rxcBrain
    .getAllCurrentParameterStep()
    .find((par) => par.key === 'type')?.value;
  return typeSelection === configFrameOnly;
};

// export const getCorrectKey = (
//   key: string,
//   isEyeglasses: boolean,
//   configPackages,
//   configData
// ) => {
//   let ret: any = null;
//   switch (key) {
//     case "Type":
//       ret = "type";
//       break;
//     case "AdvancedPrescription":
//       ret = "prescription";
//       break;
//     case "Brand":
//       ret = "brand";
//       break;
//     case "LensBundle":
//       ret = "bundles";
//       break;
//     case "Thickness":
//       ret = "thickness";
//       break;
//     case "DesignType":
//       ret = "design-type";
//       break;
//     case "LensColour":
//       ret = isEyeglasses ? "sun-step" : "lens-color";
//       break;
//     case "TreatmentsFamily":
//       ret = "treatment-need";
//       break;
//     case "GVPTreatment":
//       ret = "gvp-selection";
//       break;
//     case "ProtectionPlan":
//       ret = "warrantyOptions";
//       break;
//     case "AddOns":
//       ret = "treatment-addon";
//       break;
//     case "Review":
//       ret = isFrameOnlySelected(configPackages, configData)
//         ? "review-frameOnly"
//         : "review";
//       break;
//   }
//   return ret;
// };

export function getPosition(position: string) {
  switch (position) {
    case 'top-left':
      return 'tw-absolute tw-top-8 tw-left-8';
    case 'top-right':
      return 'tw-absolute tw-top-8 tw-right-8';
    case 'bottom-left':
      return 'tw-absolute tw-bottom-4 tw-left-4';
    case 'bottom-right':
      return 'tw-absolute tw-bottom-4 tw-right-4';
    case 'center':
      return 'tw-self-center';
  }
}

export function smoothScroll(element, target, duration) {
  const start = element.scrollTop;
  const change = target - start;
  let startTime = null;

  function animation(currentTime) {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);

    element.scrollTop = start + change * easeInOutQuad(progress);

    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    }
  }

  function easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  requestAnimationFrame(animation);
}

export const isBundlePriceEnabled = (config: any) => {
  if (config.layoutSettings?.showFramePlusLensesPrice) {
    return false;
  }
  return false; // returns undefined (falsy) in case is not specified
};

export function getChannelFromBrand(brand: string) {
  switch (brand.toLowerCase()) {
    case 'nuance':
      return 'NUA';
    case 'arnette':
      return 'AN';
    case 'clearly':
      return 'CL';
    case 'costa':
      return 'CM';
    case 'davidclulow':
      return 'DC';
    case 'default':
      return 'LC';
    case 'framesdirect':
      return 'FD';
    case 'glasses':
      return 'GL';
    case 'oakley':
      return 'OO';
    case 'oliverpeoples':
      return 'OP';
    case 'opsm':
      return 'OPSM';
    case 'osi':
      return 'OSI';
    case 'persol':
      return 'PO';
    case 'rayban':
      return 'RB';
    case 'sgh':
      return 'SGH';
    case 'targetoptical':
      return 'TO';
    default:
      return 'LC';
  }
}
export const env = () => {
  const scripts = Array.from(document.getElementsByTagName('script'));
  const rxcScript = scripts.find((script) => {
    return script?.src?.includes('rxc.js');
  });
  //const splittedScript = rxcScript?.src.split(/[//]/);
  const firstSplit = rxcScript?.src.split('/rxc3/fe/');
  if (firstSplit && firstSplit.length > 1) {
    const secondSplit = firstSplit[1].split('/');
    const env = secondSplit[0];
    return env;
  }
  return 'test';
};

export function isLocalHost() {
  return (
    window.location.href.indexOf('localhost') !== -1 ||
    window.location.href.indexOf('127.0.0.1') !== -1
  );
}

export const extractJsonfromZip = async (file, callback) => {
  const zip = new JSZip();
  const extractedFiles = await zip.loadAsync(file);
  extractedFiles.forEach(async (relativePath, file) => {
    const content = await file.async('string');
    callback(JSON.parse(content));
  });
};

export const getCookieByName = (name, isBoolean?) => {
  var nameEQ = name + '=';
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0)
      return isBoolean
        ? c.substring(nameEQ.length, c.length).toLowerCase() === 'true'
        : c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const mapConfigStepToStep = (source: any[]) => {
  if (source) {
    const configStep = source.map((s, index) => ({
      key: s.key,
      title: s.title,
      analytics_ID: s.analytics_ID,
      analytics_section: s.analytics_section,
      step_id: s.step_id,
      progressive: index + 1,
    }));
    let steps = configStepAdapter.adaptList(configStep);
    steps = steps.map((step) => {
      step.params = [];
      step.navigated = false;
      return step;
    });
    return steps;
  }
  return null;
};

export const getFrameOnlyKey = (packages?: any[], configData?: any) => {
  if (packages && packages.length && configData) {
    let frameOnlyPackage = packages.find(
      (pkg) =>
        pkg.lensPackage.upc &&
        (configData.frameOnlyLensUPC
          ? pkg.lensPackage.upc === configData?.frameOnlyLensUPC
          : pkg.lensPackage.upc ===
            window.rxcBrain.flowDefiningAttributes?.frameOnlyLensUPC)
    );
    if (frameOnlyPackage) {
      return frameOnlyPackage.lensPackage.type;
    }
  }
};

export const cleanRule = (lensPackage: any) =>
  (!lensPackage.color || !lensPackage.color?.length) &&
  (!lensPackage.blueLight || !lensPackage.blueLight?.length) &&
  (!lensPackage.transition || !lensPackage.transition?.length);

export const blueLightRule = (lensPackage: any) =>
  lensPackage.blueLight &&
  lensPackage.blueLight?.length &&
  (!lensPackage.transition || !lensPackage.transition?.length);

export const transitionRule = (lensPackage: any) =>
  lensPackage.transition &&
  lensPackage.transition?.length &&
  (lensPackage.color || lensPackage.color?.length);

export const sunRule = (lensPackage: any) =>
  lensPackage?.color &&
  lensPackage?.color?.length &&
  (!lensPackage.transition || !lensPackage.transition.length);

export function currentCategory(packages: LensPackage[]) {
  const pkgs = packages?.filter(
    (pkg: LensPackage) => pkg.lensPackage?.type?.toLowerCase() !== 'frame only'
  );
  return (
    (pkgs?.every((p) => sunRule(p.lensPackage)) && 'SUNGLASSES') ||
    (pkgs?.every((p) => !sunRule(p.lensPackage)) && 'EYEGLASSES') ||
    'MIX&MATCH'
  );
}

export const filterExistingProperties = (
  obj: Record<string, any>,
  filters?: Record<string, any>,
  isGVP?: boolean
): boolean => {
  let ret = true;

  // if (
  //   isGVP &&
  //   filters &&
  //   Object.keys(filters).length &&
  //   (Object.keys(filters)[0] === "blueLight" ||
  //     Object.keys(filters)[0] === "antiReflective")
  // ) {
  //   //gvp treatments are found under treatment in the lensPackage
  //   filters = {
  //     treatment: filters[Object.keys(filters)[0]],
  //   };
  // }

  if (filters && Object.keys(filters).length) {
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'familyFilter') {
        let hasRequired = value.required.every((val) => obj[val]);
        let doesntHaveExcluded = value.excluded.every(
          (val) => !obj[val] || obj[val].toString().length === 0
        );

        ret = ret && hasRequired && doesntHaveExcluded;
        // per supernova siamo al primo step dove c'è ancora il pacchetto frame only
        //poiche ret viene valorizzato sopra a true, non entra ne nel primo if ne nel else
        //quindi andiamo a toglierlo manualmente
      } else {
        if (Object.keys(obj).some((o) => o === key)) {
          ret = ret && obj[key] === parseOptionValue(value);
        } else {
          if (key === 'lensType' && !obj[key]) ret = false;
        }
      }
    });
  }
  return ret;
};

export const getCombinedPower = (prescription: FullPrescription) => {
  let A = parseFloat(String(prescription.SPH?.OD || 0));
  A +=
    prescription.rxType === 'READING'
      ? parseFloat(String(prescription.ADD?.OD || 0))
      : 0;

  const B =
    A +
    (prescription.CYL?.OD !== undefined && prescription.CYL?.OD !== null
      ? parseFloat(String(prescription.CYL.OD))
      : 0.0);

  let C = parseFloat(String(prescription.SPH?.OS || 0));
  C +=
    prescription.rxType === 'READING'
      ? parseFloat(String(prescription.ADD?.OS || 0))
      : 0;

  const D =
    C +
    (prescription.CYL?.OS !== undefined && prescription.CYL?.OS !== null
      ? parseFloat(String(prescription.CYL.OS))
      : 0.0);

  let values = [A, B, C, D];
  let maxIndex = 0;

  values.forEach((value, index) => {
    if (Math.abs(value) > Math.abs(values[maxIndex])) {
      maxIndex = index;
    }
  });

  return values[maxIndex];
};

export const hasValidPrescription = (
  currentPackage: LensPackage,
  prescription: FullPrescription
) => {
  if (currentPackage.lensPackage.type === 'Frame Only') {
    return true;
  }
  if (!prescription) return true;
  if (
    prescription?.prescriptionFlow &&
    !['manual', 'nested_ca', 'nested', 'account'].includes(
      prescription.prescriptionFlow?.toString().toLowerCase()
    )
  ) {
    return true;
  }

  const nullValues = [null, undefined, NaN];
  const sphMin = parseFloat(currentPackage.lensPackage.rxValues?.sph?.min);
  const sphMax = parseFloat(currentPackage.lensPackage.rxValues?.sph?.max);
  const cylMin = parseFloat(currentPackage.lensPackage.rxValues?.cyl?.min);
  const cylMax = parseFloat(currentPackage.lensPackage.rxValues?.cyl?.max);
  const addMin = parseFloat(currentPackage.lensPackage.rxValues?.add?.min);
  const addMax = parseFloat(currentPackage.lensPackage.rxValues?.add?.max);

  if (
    nullValues.includes(sphMin) ||
    nullValues.includes(sphMax) ||
    nullValues.includes(cylMin) ||
    nullValues.includes(cylMax)
  ) {
    return false;
  }

  const combinedTotalPower = getCombinedPower(prescription);
  if (
    combinedTotalPower >= sphMin &&
    combinedTotalPower <= sphMax &&
    (!prescription.CYL ||
      (([null, undefined, NaN].includes(prescription.CYL?.OD)
        ? 0
        : parseFloat(String(prescription.CYL?.OD))) >= cylMin &&
        ([null, undefined, NaN].includes(prescription.CYL?.OD)
          ? 0
          : parseFloat(String(prescription.CYL?.OD))) <= cylMax &&
        ([null, undefined, NaN].includes(prescription.CYL?.OS)
          ? 0
          : parseFloat(String(prescription.CYL?.OS))) >= cylMin &&
        ([null, undefined, NaN].includes(prescription.CYL?.OS)
          ? 0
          : parseFloat(String(prescription.CYL?.OS))) <= cylMax))
  ) {
    // progressive case
    if (
      currentPackage.lensPackage.type === 'progressive' &&
      (prescription.ADD?.OD || prescription.ADD?.OS)
    ) {
      if (
        parseFloat(String(prescription.ADD.OD)) >= addMin &&
        parseFloat(String(prescription.ADD.OD)) <= addMax
      ) {
        return true;
      }
    } else {
      return true;
    }
  }
};

export const isFramePrescriptionCompatible = (rxValues, prescriptionObject) => {
  if (rxValues && prescriptionObject?.SPH) {
    const combinedTotalPower = getCombinedPower(prescriptionObject);
    if (
      parseFloat(rxValues.powerCombinedMin) <= combinedTotalPower &&
      parseFloat(rxValues.powerCombinedMax) >= combinedTotalPower
    ) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

export const getSimplePrescriptionValuesArray = (
  start: number,
  end: number,
  incrementValue: number,
  noValueSymbol?: string | null,
  toFixed?: number,
  values?: string[]
) => {
  let valuesArray: {
    name: string | number;
    value: string | number;
    default: boolean;
  }[] = [];
  if (noValueSymbol) {
    valuesArray.push({ name: noValueSymbol, value: '', default: true });
  }

  if (values) {
    //it's the non-numeric baseVert and baseHor fields
    values.map((val) => {
      valuesArray.push({ name: val, value: val, default: false });
    });
  } else {
    for (let i = start; i <= end; i += incrementValue) {
      valuesArray.push({
        name: toFixed ? Number(i).toFixed(toFixed) : i,
        value: i,
        default: false,
      });
    }
  }

  return valuesArray;
};
export const getPrismTableColumns = (prescriptionDefaults) => {
  return (
    verticalName,
    horizontalName,
    baseDirectionName,
    upLabel,
    downLabel,
    inLabel,
    outLabel
  ) => [
    {
      id: 'vertical',
      type: 'vertical',
      placeholder: '-',
      required: true,
      name: verticalName,
      min: prescriptionDefaults.prismVertMin,
      max: prescriptionDefaults.prismVertMax,
      res: prescriptionDefaults.prismVertRes,
      rows: [
        {
          id: 'right_eye_vertical',
        },
        {
          id: 'left_eye_vertical',
        },
      ],
    },
    {
      id: 'baseDirectionVert',
      type: 'baseDirectionVert',
      placeholder: '-',
      required: true,
      name: baseDirectionName,
      values:
        upLabel && downLabel
          ? [upLabel, downLabel]
          : prescriptionDefaults.prismBaseVert,
      rows: [
        {
          id: 'right_eye_baseDirVert',
        },
        {
          id: 'left_eye_baseDirVert',
        },
      ],
    },
    {
      id: 'horizontal',
      type: 'horizontal',
      placeholder: '-',
      required: true,
      name: horizontalName,
      min: prescriptionDefaults.prismHorMin,
      max: prescriptionDefaults.prismHorMax,
      res: prescriptionDefaults.prismHorRes,
      rows: [
        {
          id: 'right_eye_horizontal',
        },
        {
          id: 'left_eye_horizontal',
        },
      ],
    },
    {
      id: 'baseDirectionHor',
      type: 'baseDirectionHor',
      placeholder: '-',
      required: true,
      name: baseDirectionName,
      values:
        inLabel && outLabel
          ? [inLabel, outLabel]
          : prescriptionDefaults.prismBaseHor,
      rows: [
        {
          id: 'right_eye_baseDirHor',
        },
        {
          id: 'left_eye_baseDirHor',
        },
      ],
    },
  ];
};

export const checkGVP = (packages?: any[]) => {
  return packages?.some((p) => p.frame?.promoType === 'GVP');
};

////////////////////////////////////////////////////////////////////////

// YOUR SELECTIONS AND REVIEW

export const getStepSelections = (
  //alias: (id: string) => string,
  getSkipped?: boolean,
  content?: Record<string, any>,
  all?: boolean
) => {
  let p: any[] = [];
  const stepsNotSkipped = window.rxcBrain.getPrevAndCurrentChain();

  const stepChain = window.rxcBrain.stepChain;
  let prevSteps = stepsNotSkipped;
  if (getSkipped) {
    prevSteps = window.rxcBrain.getPrevAndCurrentChainIgnoreSkip();
  }
  if (all) {
    prevSteps = window.rxcBrain.getNextAndCurrentChain();
  }
  const prevStepsKey = prevSteps.map(
    (s: Step) =>
      stepChain.find((sc: Step) => sc.key.toLowerCase() === s.key.toLowerCase())
        .title
  );

  prevSteps.forEach((s: Step) => {
    if (s.params) {
      let newParams = s.params
        .map((param) => {
          if (param?.key?.toLowerCase() === 'type') {
            if (param?.optionalAttributes?.value === 'Reading') {
              return {
                // ...param,
                key: param?.key,
                value: content?.type?.Reading?.title,
              };
            }
            if (param?.optionalAttributes?.value === 'Distance') {
              return {
                // ...param,
                key: param?.key,
                value: content?.type?.Distance?.title,
              };
            }
          }

          if (param.key == 'transition') {
            return {
              ...param,
              key: 'treatments',
              value: (param as any).title || param.value,
            };
          }
          if (param.key == 'GVPtreatment') {
            return {
              ...param,
              key: 'gvpTreatment',
              value: (param as any).title || param.value,
            };
          }
          if (param.key === 'addOns') {
            let ret = new Parameter();
            Object.entries(param).forEach(([key, val]) => {
              if (key === 'value') {
                if (val) {
                  ret[key] = val.value;
                } else {
                  ret[key] = null;
                }
              } else {
                ret[key] = val;
              }
            });
            return ret;
          }

          return param;
        })
        .filter((np) =>
          prevStepsKey.some((psk: string) =>
            np?.key?.toLowerCase() === 'color'
              ? true
              : np?.key?.toLowerCase() === 'type'
                ? psk.toLowerCase() === 'mixandmatch' ||
                  np?.key?.toLowerCase() === psk.toLowerCase()
                : np?.key?.toLowerCase() === psk.toLowerCase()
          )
        )
        .map((p) => {
          //const key = alias(p.key);
          const key = p.key;
          const skipped = !stepsNotSkipped.some((psk: Step) =>
            key?.toLowerCase() === 'type'
              ? psk.key.toLowerCase() === 'mixandmatch' ||
                key?.toLowerCase() === psk.title.toLowerCase()
              : p.key?.toLowerCase() == psk.title.toLowerCase() ||
                p.key?.toLowerCase() == 'color'
          );
          if (skipped) {
            if (!p.attributes) {
              p.attributes = {};
            }
            p.attributes.skipped = skipped;
          }

          return {
            ...p,
            key,
            originalStep: p.key,
          };
        });

      if (s.params.find((p) => p.key === 'color')) {
        const color = s.params.find((p) => p.key === 'color');

        if (!newParams.find((p) => p.key === 'color')) {
          newParams.push(color);
        } else {
          const colorParam = newParams.find(
            (param) => param.key?.toLowerCase() === 'color'
          );

          const relatedKeys = [
            'gvptreatment',
            'treatmentsfamily',
            'treatments',
          ];

          const hasRelated = relatedKeys.some((key) =>
            newParams.some((param) => param.key?.toLowerCase() === key)
          );

          if (colorParam && hasRelated) {
            colorParam.attributes = colorParam.attributes || {};
            colorParam.attributes.skipped = true;
          }
        }
      }
      if (s.params.find((p) => p.key === 'brand')) {
        const brand = s.params.find((p) => p.key === 'brand');

        if (!newParams.find((p) => p.key === 'brand')) {
          newParams.push(brand);
        }
      }

      if (s.params.find((p) => p.key === 'designType')) {
        const designType = s.params.find((p) => p.key === 'designType');
        const designTypeParam = {
          ...designType,
          attributes: {
            skipped: true,
          },
        };

        newParams.push(designTypeParam);

        // Controlla che ci siano entrambi ed eventualmente inverti l'ordine
        if (
          newParams.find((p) => p.key === 'designType') &&
          newParams.find((p) => p.key === 'thickness')
        ) {
          const designTypeIndex = newParams.findIndex(
            (p) => p.key === 'designType'
          );
          const thicknessIndex = newParams.findIndex(
            (p) => p.key === 'thickness'
          );

          const temp = newParams[designTypeIndex];
          newParams[designTypeIndex] = newParams[thicknessIndex];
          newParams[thicknessIndex] = temp;
        }
      }

      if (s.params.find((p) => p.key === 'thickness')) {
        const thickness = s.params.find((p) => p.key === 'thickness');

        if (!newParams.find((p) => p.key === 'thickness')) {
          newParams.push(thickness);
        }
      }

      if (s.params.find((p) => p.key === 'gvpTreatment')) {
        const gvpTreatment = s.params.find((p) => p.key === 'gvpTreatment');

        if (!newParams.find((p) => p.key === 'gvpTreatment')) {
          newParams.push(gvpTreatment);
        }
      }

      if (s.params.find((p) => p.key === 'treatmentsFamily')) {
        const treatmentsFamily = s.params.find(
          (p) => p.key === 'treatmentsFamily'
        );

        if (!newParams.find((p) => p.key === 'treatmentsFamily')) {
          newParams.push(treatmentsFamily);
        }
      }
      p = [...p, ...newParams];
    }
  });

  if (content) {
    const adapter = new ParameterToParameterWithTitleAdapter(content);
    p = adapter.adaptList(p);
  }

  return uniqBy(p, 'key');
};

export const editStep = (key: string) => {
  // if (mixAndMatch && p.id === "type") {
  //   p.id = "MixAndMatch";
  // }
  const stepBack = window.rxcBrain.findStepIdFromSelectionKey(key);

  // const GVPTreatmentStep = window.rxcBrain.findByStepKey(getAliasStep("GVPTreatment"));
  // if (stepBack && stepBack.progressive <= GVPTreatmentStep?.progressive) {
  //   reduxDispatch(workflowActions.setParameterPrice(null));
  // }
  // const LensColorStep = window.rxcBrain.findByStepKey(getAliasStep("LensColor"));
  // if (stepBack && stepBack.progressive < LensColorStep?.progressive) {
  //   reduxDispatch(colorActions.setShowFallbackImageMessage(false));
  //   reduxDispatch(colorActions.setShowNotFoundImageMessage(false));
  // }
  if (stepBack) {
    //reduxDispatch(workflowActions.setIsEditMode(true));
    fold(
      () => {},
      (newStep: Step) => {
        window.rxcBrain.currentStep = newStep;
      }
    )(window.rxcBrain.backToStepByKey(stepBack.key));
  }
};

////////////////////////////////////////////////////////////////////////

// CMD
export const getStateOptionsByCountry = ({ country }: { country: string }) => {
  const countryCode = country.split('_')[1];
  const US_Options = [
    'State*',
    'AL',
    'AK',
    'AS',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'DC',
    'FL',
    'FM',
    'GA',
    'GU',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'MA',
    'MD',
    'ME',
    'MH',
    'MI',
    'MN',
    'MP',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'PR',
    'PW',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UM',
    'UT',
    'VA',
    'VI',
    'VT',
    'WA',
    'WI',
    'WV',
    'WY',
  ];
  const CA_Options = [
    'Province*',
    'AB',
    'BC',
    'MB',
    'NB',
    'NL',
    'NT',
    'NS',
    'NU',
    'ON',
    'PE',
    'QC',
    'SK',
    'YT',
  ];
  // en_US es_US en_CA fr_CA
  const stateOptions = countryCode === 'US' ? US_Options : CA_Options;
  return stateOptions;
};

export function useReleaseVersion() {
  const scripts = Array.from(document.getElementsByTagName('script'));
  const rxcScript = scripts.find((script) => {
    return script?.src?.includes('rxc.js');
  });
  const splittedScript = rxcScript?.src.split(/[//]/);
  const version = splittedScript?.[6];
  return version;
}

export function getConfig(configContent: any, stepName: string) {
  let ret = [];

  const key = Object.keys(configContent).find(
    (el) => el.toLowerCase() === stepName.toLowerCase()
  );

  if (key) {
    const entries = configContent[key];
    if (entries) {
      ret = Object.entries<Record<string, any>>(entries).map(([key, val]) => ({
        id: key,
        ...val,
      }));
    }
  }
  ret = orderBy(ret, 'sequence');
  return ret;
}

export const hasAttribute = (component: any, attribute: string): boolean => {
  // Controlla se l'attributo del componente è quello richiesto
  if (component?.attribute === attribute) {
    return true;
  }

  // Se il componente ha nestedComponents, controlla ricorsivamente
  if (component?.nestedComponents && component?.nestedComponents.length > 0) {
    return component.nestedComponents.some((nestedComponent: any) =>
      hasAttribute(nestedComponent, attribute)
    );
  }

  return false;
};

export const getUrlSwatch = (
  value: string,
  imageSrcsetTemplate: string,
  configProjectImage: string
) => {
  let format = value?.indexOf('.') > 0 ? 'svg' : 'png';
  return imageSrcsetTemplate
    .replace('{url}', configProjectImage)
    .replace('{url}', configProjectImage)
    .replace('{filename}', value)
    .replace('{filename}', value)
    .replace('{format}', format)
    .replace('{format}', format);
};

export const getReviewObjectForMixAndMatch = (confirmedTitles) => {
  const reviewObject = {};

  const selections = window?.rxcBrain?.mixAndMatchSelections || [];

  selections.forEach((item, index) => {
    const [key, value] = Object.entries(item)[0] || [];

    if (!key || !value) return;

    const lowerKey = key.toLowerCase();
    const isTreatment =
      lowerKey.includes('bluelight') || lowerKey.includes('transition');

    const confirmedTitleKey = `${isTreatment ? 'treatmentsFamily' : key}_confirmedTitle`;

    const name = confirmedTitles?.[confirmedTitleKey] || '';
    const description = value?.title || '';

    if (!name || !description) return;

    const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);

    reviewObject[capitalizedKey] = {
      sequence: Object.keys(reviewObject).length + 1,
      name,
      description,
    };
  });

  const selectedPackage = getCheapPackageFromList(
    window?.rxcBrain?.superNovaCurrentFilteredPackages
  );

  const attributeListToReview = ['thickness', 'designType', 'antiReflective'];
  const content = window?.rxcBrain?.config?.lensesData?.content;

  attributeListToReview.forEach((attribute) => {
    const value = selectedPackage?.lensPackage?.[attribute];
    if (!value) return;

    const key = attribute === 'antiReflective' ? 'addOns' : attribute;
    const confirmedTitleKey = `${key}_confirmedTitle`;
    const name = confirmedTitles?.[confirmedTitleKey] || '';
    const description = content?.[attribute]?.[value]?.title || value || '';
    const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);

    if (!name || !description) return;

    reviewObject[capitalizedKey] = {
      sequence: Object.keys(reviewObject).length + 1,
      name,
      description,
    };
  });

  console.log('reviewObject M&M:', reviewObject);
  return reviewObject;
};

export function parseOptionValue(value: string | boolean) {
  if (typeof value !== 'string') return value;
  const lower = value.toLowerCase();
  if (lower === 'true') return true;
  if (lower === 'false') return false;
  return value;
}
